import { supabaseClient } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";

interface SecretaryOrganisationData {
    secretaryId: string;
    ownerClinicianId: string;
    organisationId: string;
}

export const addSecretaryToOrganisation = async (data: SecretaryOrganisationData) => {
    // secretary_organisation_assignments has a composite FK to secretary_owners, so
    // "choose one from their organisation(s)" (a secretary the clinician has never
    // owned before) needs that base link created first, not just assumed to exist.
    // secretary_owners has no UPDATE policy (nothing on it is ever meant to be updated - a row
    // either links an owner or it doesn't), so a plain upsert's default ON CONFLICT DO UPDATE
    // gets rejected by RLS whenever the clinician already owns this secretary via another
    // organisation. ignoreDuplicates makes it ON CONFLICT DO NOTHING instead.
    const { error: ownerError } = await supabaseClient.from("secretary_owners").upsert(
        {
            secretary_id: data.secretaryId,
            owner_clinician_id: data.ownerClinicianId,
        },
        { onConflict: "secretary_id,owner_clinician_id", ignoreDuplicates: true }
    );

    if (ownerError) throw new Error(ownerError.message);

    // First secretary a clinician adds for an org becomes its default - minimises clicks
    // for the common case (one secretary per organisation).
    const { count, error: countError } = await supabaseClient
        .from("secretary_organisation_assignments")
        .select("secretary_id", { count: "exact", head: true })
        .eq("owner_clinician_id", data.ownerClinicianId)
        .eq("organisation_id", data.organisationId);

    if (countError) throw new Error(countError.message);

    const { error } = await supabaseClient.from("secretary_organisation_assignments").insert({
        secretary_id: data.secretaryId,
        owner_clinician_id: data.ownerClinicianId,
        organisation_id: data.organisationId,
        is_default: count === 0,
    });

    if (error) throw new Error(error.message);
    return data;
};

export const setDefaultSecretaryForOrganisation = async (data: SecretaryOrganisationData) => {
    // Team "Set as default" is shown for any secretary in the org, including people
    // who are not yet this clinician's assigned secretary. Assign them first so the
    // default RPC does not fail with "Secretary is not assigned...".
    const { error: ownerError } = await supabaseClient.from("secretary_owners").upsert(
        {
            secretary_id: data.secretaryId,
            owner_clinician_id: data.ownerClinicianId,
        },
        { onConflict: "secretary_id,owner_clinician_id", ignoreDuplicates: true }
    );

    if (ownerError) throw new Error(ownerError.message);

    const { data: existing, error: existingError } = await supabaseClient
        .from("secretary_organisation_assignments")
        .select("secretary_id")
        .eq("secretary_id", data.secretaryId)
        .eq("owner_clinician_id", data.ownerClinicianId)
        .eq("organisation_id", data.organisationId)
        .maybeSingle();

    if (existingError) throw new Error(existingError.message);

    if (!existing) {
        // Insert as non-default: another secretary may already be default for this
        // clinician+org. The RPC below moves the flag atomically.
        const { error: insertError } = await supabaseClient.from("secretary_organisation_assignments").insert({
            secretary_id: data.secretaryId,
            owner_clinician_id: data.ownerClinicianId,
            organisation_id: data.organisationId,
            is_default: false,
        });

        if (insertError) throw new Error(insertError.message);
    }

    const { error } = await supabaseClient.rpc("set_default_secretary_for_organisation", {
        p_owner_clinician_id: data.ownerClinicianId,
        p_organisation_id: data.organisationId,
        p_secretary_id: data.secretaryId,
    });

    if (error) throw new Error(error.message);
    return data;
};

export const clearDefaultSecretaryForOrganisation = async (data: SecretaryOrganisationData) => {
    const { error } = await supabaseClient.rpc("clear_default_secretary_for_organisation", {
        p_owner_clinician_id: data.ownerClinicianId,
        p_organisation_id: data.organisationId,
        p_secretary_id: data.secretaryId,
    });

    if (error) throw new Error(error.message);
    return data;
};

export const removeSecretaryFromOrganisation = async (data: SecretaryOrganisationData) => {
    const { error } = await supabaseClient
        .from("secretary_organisation_assignments")
        .delete()
        .eq("secretary_id", data.secretaryId)
        .eq("owner_clinician_id", data.ownerClinicianId)
        .eq("organisation_id", data.organisationId);

    if (error) throw new Error(error.message);
    return data;
};

function invalidateSecretaryOrganisations(queryClient: ReturnType<typeof useQueryClient>, ownerClinicianId: string) {
    queryClient.invalidateQueries({ queryKey: ["secretary-organisations", ownerClinicianId] });
    queryClient.invalidateQueries({ queryKey: ["team-members"] });
}

export const useAddSecretaryToOrganisation = () => {
    const toastCopy = useToastCopy();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: addSecretaryToOrganisation,
        onSuccess: (data) => {
            toast.success(toastCopy.secretaryAddedToOrganisation);
            invalidateSecretaryOrganisations(queryClient, data.ownerClinicianId);
        },
        onError: (error: Error) => {
            toast.error(toastCopy.errorAddingSecretaryToOrganisation(error.message));
        },
    });
};

export const useSetDefaultSecretaryForOrganisation = () => {
    const toastCopy = useToastCopy();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: setDefaultSecretaryForOrganisation,
        onSuccess: (data) => {
            toast.success(toastCopy.defaultSecretarySet);
            invalidateSecretaryOrganisations(queryClient, data.ownerClinicianId);
        },
        onError: (error: Error) => {
            toast.error(toastCopy.errorSettingDefaultSecretary(error.message));
        },
    });
};

export const useClearDefaultSecretaryForOrganisation = () => {
    const toastCopy = useToastCopy();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: clearDefaultSecretaryForOrganisation,
        onSuccess: (data) => {
            toast.success(toastCopy.defaultSecretaryCleared);
            invalidateSecretaryOrganisations(queryClient, data.ownerClinicianId);
        },
        onError: (error: Error) => {
            toast.error(toastCopy.errorClearingDefaultSecretary(error.message));
        },
    });
};

export const useRemoveSecretaryFromOrganisation = () => {
    const toastCopy = useToastCopy();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: removeSecretaryFromOrganisation,
        onSuccess: (data) => {
            toast.success(toastCopy.secretaryRemovedFromOrganisation);
            invalidateSecretaryOrganisations(queryClient, data.ownerClinicianId);
        },
        onError: (error: Error) => {
            toast.error(toastCopy.errorRemovingSecretaryFromOrganisation(error.message));
        },
    });
};
